// Base utilities for API requests
export class BaseApiClient {
  protected baseUrl: string

  constructor(baseUrl: string = '/api') {
    this.baseUrl = baseUrl
  }

  protected async request(endpoint: string, options: RequestInit = {}): Promise<any> {
    const url = `${this.baseUrl}${endpoint}`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    
    // Add any additional headers from options
    if (options.headers) {
      Object.assign(headers, options.headers)
    }

    // Get fresh token from localStorage on each request
    const token = localStorage.getItem('auth_token')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(url, {
      ...options,
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      // Include error code in message so callers can check for specific errors
      const errorCode = errorData.error || ''
      const errorMessage = errorData.message || `HTTP ${response.status}`
      throw new Error(errorCode ? `${errorCode}: ${errorMessage}` : errorMessage)
    }

    return response.json()
  }

  protected async requestFormData(
    endpoint: string,
    formData: FormData,
    options: RequestInit = {}
  ): Promise<any> {
    const url = `${this.baseUrl}${endpoint}`
    const headers: Record<string, string> = {}

    // Get fresh token from localStorage
    const token = localStorage.getItem('auth_token')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    // Merge with any additional headers from options
    // Note: Don't set Content-Type for FormData - browser will set it with boundary
    if (options.headers) {
      const additionalHeaders = typeof options.headers === 'object' && !(options.headers instanceof Headers)
        ? options.headers as Record<string, string>
        : {}
      Object.keys(additionalHeaders).forEach(key => {
        if (key.toLowerCase() !== 'content-type') {
          headers[key] = additionalHeaders[key]
        }
      })
    }

    const response = await fetch(url, {
      ...options,
      method: options.method || 'POST',
      headers,
      body: formData,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`)
    }

    return response.json()
  }

  protected async downloadFile(
    endpoint: string,
    defaultFilename: string = 'download'
  ): Promise<void> {
    const url = `${this.baseUrl}${endpoint}`
    const headers: Record<string, string> = {}

    const token = localStorage.getItem('auth_token')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(url, { headers })
    if (!response.ok) {
      // Try to parse JSON error response
      const contentType = response.headers.get('Content-Type')
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json().catch(() => ({}))
        const error = new Error(errorData.error || 'Download failed') as Error & { 
          error_code?: string
          downloads_used?: number
          downloads_limit?: number
        }
        error.error_code = errorData.error_code
        error.downloads_used = errorData.downloads_used
        error.downloads_limit = errorData.downloads_limit
        throw error
      }
      throw new Error('Download failed')
    }
    
    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl
    
    // Try to get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = defaultFilename
    if (contentDisposition) {
      const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition)
      if (matches != null && matches[1]) {
        filename = matches[1].replace(/['"]/g, '')
      }
    }
    
    a.download = filename
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(downloadUrl)
    document.body.removeChild(a)
  }
}

